/**
 * Manutenção do catálogo de agentes nocivos (#458): quem cuida do catálogo acha o agente,
 * vê de relance o que está incompleto e abre a ficha para corrigir.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import {
  hazardousAgentService,
  type HazardousAgentSummary,
  type HazardousAgentStatus,
} from '../../services/hazardousAgentService';
import {
  HazardousAgentFilters,
  type HazardousAgentFiltersData,
} from './components/HazardousAgentFilters';
import { DEGREE_LABELS } from './degreeLabels';

const columns: DataTableColumn<HazardousAgentSummary>[] = [
  {
    id: 'displayName',
    label: 'Agente',
    primary: true,
    render: (agent) => (
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {agent.displayName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          CAS: {agent.casNumber ?? '—'} · eSocial: {agent.esocialCode ?? '—'}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'degree',
    label: 'Grau de insalubridade',
    render: (agent) =>
      agent.unhealthyDegree ? (
        <Typography variant="body2">{DEGREE_LABELS[agent.unhealthyDegree] ?? agent.unhealthyDegree}</Typography>
      ) : (
        <Chip size="small" color="warning" variant="outlined" label="Sem grau definido" />
      ),
  },
  {
    id: 'limits',
    label: 'Opções de limite',
    render: (agent) =>
      agent.toleranceLimitCount === 0 ? (
        <Chip size="small" color="warning" variant="outlined" label="Sem opção de limite" />
      ) : (
        <Typography variant="body2" color="text.secondary">
          {agent.toleranceLimitCount === undefined ? '—' : agent.toleranceLimitCount}
        </Typography>
      ),
  },
  {
    id: 'status',
    label: 'Situação',
    align: 'center',
    render: (agent) =>
      agent.status === 'ACTIVE' ? (
        <Chip size="small" color="success" label="Ativo" />
      ) : (
        <Chip size="small" label="Inativo" />
      ),
  },
];

export const HazardousAgentListPage: React.FC = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<HazardousAgentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<{ q?: string; status?: HazardousAgentStatus }>({});

  // Na busca ao vivo uma resposta lenta pode chegar depois de uma mais nova: só a última vale.
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    try {
      setLoading(true);
      setFailed(false);
      const response = await hazardousAgentService.list({ page: page + 1, limit: rowsPerPage, ...filters });
      if (seq !== requestSeq.current) return;
      setAgents(response.data);
      setTotal(response.pagination.total);
    } catch {
      if (seq === requestSeq.current) setFailed(true);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFilter = useCallback((data: HazardousAgentFiltersData) => {
    const next: { q?: string; status?: HazardousAgentStatus } = {};
    if (data.searchTerm) next.q = data.searchTerm;
    if (data.status) next.status = data.status as HazardousAgentStatus;
    // O filtro aplica ao abrir a tela com tudo vazio; manter o objeto evita uma segunda busca igual.
    setFilters((current) => (current.q === next.q && current.status === next.status ? current : next));
    setPage(0);
  }, []);

  const filtersActive = Boolean(filters.q || filters.status);
  const open = (agent: HazardousAgentSummary) => navigate(`/hazardous-agents/${agent.id}`);

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Agentes Nocivos' }]}
      title="Agentes Nocivos"
      subtitle="Confira e corrija o grau, o anexo e os limites de cada agente do catálogo"
    >
      {failed && !loading && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Tentar de novo
            </Button>
          }
        >
          Não foi possível carregar os agentes. Confira sua conexão e tente de novo.
        </Alert>
      )}

      <Stack spacing={3}>
        <HazardousAgentFilters onFilter={handleFilter} loading={loading} total={total} />

        <DataTable
          columns={columns}
          rows={agents}
          getRowId={(agent) => agent.id}
          loading={loading}
          filtersActive={filtersActive}
          emptyState={{
            title: 'Nenhum agente nocivo no catálogo',
            description:
              'O catálogo reúne os agentes da NR-15 e do Anexo IV usados ao cadastrar riscos ocupacionais. Se ele está vazio, avise o suporte.',
          }}
          onRowClick={open}
          actions={[{ icon: <ViewIcon fontSize="small" />, tooltip: 'Abrir agente', onClick: open }]}
          pagination={{
            page,
            rowsPerPage,
            totalCount: total,
            onPageChange: setPage,
            onRowsPerPageChange: (value) => {
              setRowsPerPage(value);
              setPage(0);
            },
          }}
        />
      </Stack>
    </PageShell>
  );
};

export default HazardousAgentListPage;
